import { h } from 'vue';

const sizerStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  visibility: 'hidden',
  height: 0,
  overflow: 'scroll',
  whiteSpace: 'pre',
};

const INPUT_PROPS_BLACKLIST = [
  'minWidth',
  'extraWidth',
  'placeholderIsMinWidth',
  'injectStyles',
  'inputClass',
  'inputStyle',
  'sizeChange',
];

const cleanInputProps = (inputProps) => {
  const newProps = {};
  const inputPropsKeys = Object.keys(inputProps);
  inputPropsKeys.forEach((propField) => {
    if (!INPUT_PROPS_BLACKLIST.includes(propField)) {
      newProps[propField] = inputProps[propField];
    }
  });
  return newProps;
};

const copyStyles = (styles, node) => {
  const newNodeStyle = node;
  newNodeStyle.style.fontSize = styles.fontSize;
  newNodeStyle.style.fontFamily = styles.fontFamily;
  newNodeStyle.style.fontWeight = styles.fontWeight;
  newNodeStyle.style.fontStyle = styles.fontStyle;
  newNodeStyle.style.letterSpacing = styles.letterSpacing;
  newNodeStyle.style.textTransform = styles.textTransform;
  return newNodeStyle;
};

const generateId = () => `_${Math.random().toString(36).substr(2, 12)}`;

const getListeners = (context) => {
  const listeners = {};
  Object.keys(context.$attrs).forEach((attr) => {
    if (attr.startsWith('on')) {
      listeners[attr] = context.$attrs[attr];
    }
  });
  return listeners;
};

const AutosizeInput = {
  name: 'AutosizeInput',
  props: {
    // id to use for the input, can be set for consistent snapshots
    id: String,
    // minimum width for input element
    minWidth: {
      type: [String, Number],
      default: 1,
    },
    // additional width for input element
    extraWidth: [String, Number],
    // don't collapse size to less than the placeholder
    placeholderIsMinWidth: Boolean,
    // inject the custom stylesheet to hide clear UI, defaults to true
    injectStyles: {
      type: Boolean,
      default: true,
    },
    // placeholder text
    placeholder: String,
    // class for the input element
    inputClass: Array,
    // css styles for the input element
    inputStyle: Object,
    // size change callback
    sizeChange: Function,
    // field value
    value: {
      required: true,
    },
    maxWidth: {
      type: Number,
      required: false,
      default: 0,
    },
    borderLess: {
      type: Boolean,
      required: false,
      default: false,
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      inputWidth: this.minWidth,
      inputId: this.id || generateId(),
    };
  },
  watch: {
    value(val, oldVal) {
      if (val !== oldVal) {
        this.$nextTick(() => {
          this.updateInputWidth();
        });
      }
    },
    inputWidth(val, oldVal) {
      if (val !== oldVal) {
        if (this.sizeChange) this.sizeChange(val);
      }
    },
  },
  mounted() {
    this.input = this.$refs.input;
    this.sizer = this.$refs.sizer;
    this.placeHolderSizer = this.$refs.placeHolderSizer;
    this.copyInputStyles();
    this.updateInputWidth();
  },
  methods: {
    getInput() {
      return this.input;
    },
    focus() {
      this.input.focus();
    },
    blur() {
      this.clearSelection();
      this.input.blur();
    },
    select() {
      this.clearSelection();
      this.input.select();
    },
    clearSelection() {
      (window.getSelection ? window.getSelection() : document.selection).empty();
    },
    copyInputStyles() {
      if (!window.getComputedStyle) {
        return;
      }
      const inputStyles = this.input && window.getComputedStyle(this.input);
      if (!inputStyles) {
        return;
      }
      this.sizer = copyStyles(inputStyles, this.sizer);
      if (this.placeHolderSizer) {
        this.placeHolderSizer = copyStyles(inputStyles, this.placeHolderSizer);
      }
    },
    updateInputWidth() {
      if (!this.sizer || typeof this.sizer.scrollWidth === 'undefined') {
        return;
      }
      let newInputWidth;
      if (this.placeholder && (!this.value || (this.value && this.placeholderIsMinWidth))) {
        newInputWidth =
          Math.max(this.sizer.scrollWidth, this.placeHolderSizer.scrollWidth) +
          (this.borderLess ? 0 : 2);
      } else {
        newInputWidth = parseFloat(getComputedStyle(this.sizer).width) + (this.borderLess ? 0 : 2);
      }
      const extraWidth =
        this.$attrs.type === 'number' && this.extraWidth === undefined
          ? 16
          : parseInt(this.extraWidth, 10) || 0;
      newInputWidth += extraWidth;
      if (newInputWidth < this.minWidth) {
        newInputWidth = this.minWidth;
      }
      if (newInputWidth !== this.inputWidth) {
        this.inputWidth = newInputWidth;
      }
    },
  },
  render() {
    const { ...inputProps } = this.$props;
    const props = cleanInputProps(inputProps);
    return h(
      'div',
      {
        class: 'autosize-input',
        style: {
          display: 'inline-block',
        },
      },
      [
        h('input', {
          ref: 'input',
          class: this.inputClass,
          style: {
            boxSizing: 'content-box',
            width: `${this.inputWidth}px`,
            ...this.inputStyle,
            // 48px = 32px icon + 2 * 8px of padding
            maxWidth: `${this.maxWidth - 48}px`,
            textOverflow: 'ellipsis',
          },
          value: this.value,
          spellcheck: false,
          ...props,
          ...getListeners(this),
        }),
        h(
          'div',
          {
            ref: 'sizer',
            style: sizerStyle,
          },
          this.value
        ),
        h(
          'div',
          {
            ref: 'placeHolderSizer',
            style: sizerStyle,
          },
          this.placeholder
        ),
      ]
    );
  },
};

if (typeof window !== 'undefined' && 'Vue' in window) {
  window.Vue.component('input-autosize', AutosizeInput);
}

export default AutosizeInput;
